import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  Tab,
  Tabs,
  TextField,
  MenuItem,
  Tooltip,
  IconButton,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import InsightsIcon from '@mui/icons-material/Insights';
import HistoryIcon from '@mui/icons-material/History';
import DownloadIcon from '@mui/icons-material/Download';
import { bloomberg } from '../theme/bloombergTheme';
import { TickerLink } from '../components/TickerActionMenu';
import api from '../services/api';
import html2canvas from 'html2canvas';

interface Signal {
  symbol: string;
  name: string;
  signal_type: string;
  category: string;
  direction: string;
  price: number;
  detail: string;
  rsi: number | null;
  change_pct: number;
  country: string;
  sector: string;
  signal_date: string; // ISO date of the data used to detect this signal
}

const CATEGORY_LABELS: Record<string, string> = {
  milestone: 'Milestones',
  ma_cross: 'MA Crosses',
  momentum: 'Momentum',
};

const SIGNAL_LABELS: Record<string, string> = {
  '52w_high': '52W High',
  '52w_low': '52W Low',
  crossed_above_20ma: 'Above 20MA',
  crossed_below_20ma: 'Below 20MA',
  crossed_above_50ma: 'Above 50MA',
  crossed_below_50ma: 'Below 50MA',
  crossed_above_200ma: 'Above 200MA',
  crossed_below_200ma: 'Below 200MA',
  golden_cross: 'Golden Cross',
  death_cross: 'Death Cross',
  rsi_oversold: 'RSI Oversold',
  rsi_overbought: 'RSI Overbought',
};

const REGION_MAP: Record<string, Set<string>> = {
  US: new Set(['US']),
  UK: new Set(['UK', 'GB']),
  Europe: new Set(['DE', 'FR', 'NL', 'CH', 'SE', 'NO', 'DK', 'FI', 'ES', 'IT', 'PT', 'BE', 'AT', 'IE', 'PL', 'LU', 'GR', 'CZ']),
  Americas: new Set(['CA', 'BR', 'MX', 'AR', 'CL', 'CO', 'PE']),
  'Asia-Pacific': new Set(['JP', 'KR', 'CN', 'HK', 'TW', 'SG', 'IN', 'AU', 'NZ', 'TH', 'ID', 'MY', 'PH', 'VN']),
};
const ALL_REGION_COUNTRIES = new Set(Object.values(REGION_MAP).flatMap(s => Array.from(s)));

const Signals: React.FC = () => {
  const [tab, setTab] = useState<'live' | 'history'>('live');
  const exportRef = useRef<HTMLDivElement>(null);
  const [exporting, setExporting] = useState(false);
  const [activeRegions, setActiveRegions] = useState<Set<string>>(new Set(['US', 'UK', 'Europe']));

  const handleExport = useCallback(async () => {
    if (!exportRef.current) return;
    setExporting(true);
    try {
      const canvas = await html2canvas(exportRef.current, {
        backgroundColor: '#0a0a0a',
        scale: 2,
        useCORS: true,
        logging: false,
      });
      const link = document.createElement('a');
      const dateStr = new Date().toISOString().slice(0, 10);
      link.download = `market-signals-${tab}-${dateStr}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } finally {
      setExporting(false);
    }
  }, [tab]);

  // ── Live tab state ──────────────────────────────────────────────────────────
  const [signals, setSignals] = useState<Signal[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [directionFilter, setDirectionFilter] = useState<string>('all');
  const [dateFilter, setDateFilter] = useState<string>('all');
  const [marketCapFilter, setMarketCapFilter] = useState<string>('all');
  const [sectorFilter, setSectorFilter] = useState<string>('all');

  const toggleRegion = useCallback((region: string) => {
    setActiveRegions(prev => {
      const next = new Set(prev);
      if (next.has(region)) next.delete(region);
      else next.add(region);
      return next;
    });
  }, []);

  const passesRegionFilter = useCallback((country: string) => {
    if (activeRegions.size === 0) return true; // none selected = show all
    return Array.from(activeRegions).some(region => REGION_MAP[region]?.has(country));
  }, [activeRegions]);

  const fetchSignals = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      if (marketCapFilter !== 'all') params.market_cap = marketCapFilter;
      const { data } = await api.get(`/api/signals`, { params });
      setSignals(data.signals || []);
      setLastUpdated(data.last_updated);
      setDateFilter('all');
    } catch (err) {
      console.error('Error fetching signals:', err);
    } finally {
      setLoading(false);
    }
  }, [marketCapFilter]);

  useEffect(() => {
    if (tab === 'live') fetchSignals();
  }, [fetchSignals, tab]);

  // ── History tab state ───────────────────────────────────────────────────────
  const [histSignals, setHistSignals] = useState<Signal[]>([]);
  const [histLoading, setHistLoading] = useState(false);
  const [histCategory, setHistCategory] = useState<string>('all');
  const [histDirection, setHistDirection] = useState<string>('all');
  const [histDateFrom, setHistDateFrom] = useState<string>('');
  const [histDateTo, setHistDateTo] = useState<string>('');

  const fetchHistory = useCallback(async () => {
    setHistLoading(true);
    try {
      const params: Record<string, string> = {};
      if (histCategory !== 'all') params.category = histCategory;
      if (histDirection !== 'all') params.direction = histDirection;
      if (histDateFrom) params.date_from = histDateFrom;
      if (histDateTo) params.date_to = histDateTo;
      const { data } = await api.get(`/api/signals/history`, { params });
      setHistSignals(data.signals || []);
    } catch (err) {
      console.error('Error fetching signal history:', err);
    } finally {
      setHistLoading(false);
    }
  }, [histCategory, histDirection, histDateFrom, histDateTo]);

  useEffect(() => {
    if (tab === 'history') fetchHistory();
  }, [tab, fetchHistory]);

  // Collect unique sectors from all signals (live + history)
  const availableSectors = Array.from(
    new Set([...signals, ...histSignals].map((s) => s.sector).filter(Boolean))
  ).sort();

  const filteredHist = histSignals.filter((s) => {
    if (!passesRegionFilter(s.country)) return false;
    if (sectorFilter !== 'all' && s.sector !== sectorFilter) return false;
    return true;
  });

  // Collect unique signal dates sorted descending
  const availableDates = Array.from(new Set(signals.map((s) => s.signal_date).filter(Boolean)))
    .sort((a, b) => b.localeCompare(a));

  const filtered = signals.filter((s) => {
    if (categoryFilter !== 'all' && s.category !== categoryFilter) return false;
    if (directionFilter !== 'all' && s.direction !== directionFilter) return false;
    if (dateFilter !== 'all' && s.signal_date !== dateFilter) return false;
    if (!passesRegionFilter(s.country)) return false;
    if (sectorFilter !== 'all' && s.sector !== sectorFilter) return false;
    return true;
  });

  // Group by category
  const grouped = filtered.reduce<Record<string, Signal[]>>((acc, s) => {
    if (!acc[s.category]) acc[s.category] = [];
    acc[s.category].push(s);
    return acc;
  }, {});

  const categoryOrder = ['milestone', 'ma_cross', 'momentum'];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <InsightsIcon sx={{ color: bloomberg.accent.orange, fontSize: 28 }} />
          <Typography variant="h4" sx={{ fontWeight: 700, color: bloomberg.accent.orange }}>
            Market Signals
          </Typography>
          {tab === 'live' && !loading && (
            <Chip
              size="small"
              label={`${filtered.length} signals`}
              sx={{ ml: 1, height: 22, fontSize: 11, fontWeight: 600, bgcolor: bloomberg.accent.orangeDim, color: bloomberg.accent.orange }}
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {tab === 'live' && lastUpdated && (
            <Typography variant="caption" sx={{ color: bloomberg.text.muted }}>
              Updated {new Date(lastUpdated).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
            </Typography>
          )}
          <Tooltip title="Export as image">
            <IconButton
              size="small"
              onClick={handleExport}
              disabled={exporting}
              sx={{ color: bloomberg.text.muted, '&:hover': { color: bloomberg.accent.orange } }}
            >
              <DownloadIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Exportable content area */}
      <Box ref={exportRef} sx={{ bgcolor: bloomberg.bg.primary, p: 0 }}>

      {/* Tab selector */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{
          mb: 2,
          minHeight: 32,
          '& .MuiTab-root': { minHeight: 32, py: 0.5, fontSize: 12, fontWeight: 600, textTransform: 'none' },
          '& .Mui-selected': { color: `${bloomberg.accent.orange} !important` },
          '& .MuiTabs-indicator': { bgcolor: bloomberg.accent.orange },
        }}
      >
        <Tab value="live" icon={<InsightsIcon sx={{ fontSize: 15 }} />} iconPosition="start" label="Live Scan" />
        <Tab value="history" icon={<HistoryIcon sx={{ fontSize: 15 }} />} iconPosition="start" label="History" />
      </Tabs>

      {/* ── Live tab ──────────────────────────────────────────────────────────── */}
      {tab === 'live' && (<>
      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <ToggleButtonGroup
          value={categoryFilter}
          exclusive
          onChange={(_, v) => v && setCategoryFilter(v)}
          size="small"
        >
          <ToggleButton value="all" sx={{ px: 1.5, py: 0.4, fontSize: 11 }}>All</ToggleButton>
          <ToggleButton value="milestone" sx={{ px: 1.5, py: 0.4, fontSize: 11 }}>Milestones</ToggleButton>
          <ToggleButton value="ma_cross" sx={{ px: 1.5, py: 0.4, fontSize: 11 }}>MA Crosses</ToggleButton>
          <ToggleButton value="momentum" sx={{ px: 1.5, py: 0.4, fontSize: 11 }}>Momentum</ToggleButton>
        </ToggleButtonGroup>

        <ToggleButtonGroup
          value={directionFilter}
          exclusive
          onChange={(_, v) => v && setDirectionFilter(v)}
          size="small"
        >
          <ToggleButton value="all" sx={{ px: 1.5, py: 0.4, fontSize: 11 }}>All</ToggleButton>
          <ToggleButton value="bullish" sx={{ px: 1.5, py: 0.4, fontSize: 11, '&.Mui-selected': { color: bloomberg.status.positive, bgcolor: bloomberg.status.positiveBg } }}>
            Bullish
          </ToggleButton>
          <ToggleButton value="bearish" sx={{ px: 1.5, py: 0.4, fontSize: 11, '&.Mui-selected': { color: bloomberg.status.negative, bgcolor: bloomberg.status.negativeBg } }}>
            Bearish
          </ToggleButton>
        </ToggleButtonGroup>

        <ToggleButtonGroup
          value={marketCapFilter}
          exclusive
          onChange={(_, v) => v && setMarketCapFilter(v)}
          size="small"
        >
          <ToggleButton value="all" sx={{ px: 1.5, py: 0.4, fontSize: 11 }}>All Caps</ToggleButton>
          <ToggleButton value="large" sx={{ px: 1.5, py: 0.4, fontSize: 11 }}>Large Cap</ToggleButton>
          <ToggleButton value="mid" sx={{ px: 1.5, py: 0.4, fontSize: 11 }}>Mid Cap</ToggleButton>
          <ToggleButton value="small" sx={{ px: 1.5, py: 0.4, fontSize: 11 }}>Small Cap</ToggleButton>
        </ToggleButtonGroup>

        {/* Region filter — toggle on/off */}
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant="caption" sx={{ color: bloomberg.text.muted, mr: 0.5, fontWeight: 600, fontSize: 10 }}>
            REGION:
          </Typography>
          {Object.keys(REGION_MAP).map((region) => {
            const isOn = activeRegions.has(region);
            return (
              <Chip
                key={region}
                size="small"
                label={region}
                onClick={() => toggleRegion(region)}
                sx={{
                  height: 22, fontSize: 10, cursor: 'pointer', fontWeight: isOn ? 700 : 400,
                  bgcolor: isOn ? bloomberg.accent.orangeDim : bloomberg.bg.tertiary,
                  color: isOn ? bloomberg.accent.orange : bloomberg.text.secondary,
                  border: `1px solid ${isOn ? bloomberg.accent.orange : bloomberg.border.primary}`,
                }}
              />
            );
          })}
        </Box>

        {/* Sector filter dropdown */}
        <TextField
          select size="small" label="Sector"
          value={sectorFilter}
          onChange={(e) => setSectorFilter(e.target.value)}
          sx={{ minWidth: 160, '& .MuiInputBase-root': { fontSize: 11 } }}
        >
          <MenuItem value="all" sx={{ fontSize: 12 }}>All Sectors</MenuItem>
          {availableSectors.map((s) => (
            <MenuItem key={s} value={s} sx={{ fontSize: 12 }}>{s}</MenuItem>
          ))}
        </TextField>

        {/* Date filter — chips for each available signal date */}
        {availableDates.length > 0 && (
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="caption" sx={{ color: bloomberg.text.muted, mr: 0.5, fontWeight: 600, fontSize: 10 }}>
              DATE:
            </Typography>
            <Chip
              size="small"
              label="All"
              onClick={() => setDateFilter('all')}
              sx={{
                height: 22, fontSize: 10, cursor: 'pointer',
                bgcolor: dateFilter === 'all' ? bloomberg.accent.orangeDim : bloomberg.bg.tertiary,
                color: dateFilter === 'all' ? bloomberg.accent.orange : bloomberg.text.secondary,
                border: `1px solid ${dateFilter === 'all' ? bloomberg.accent.orange : bloomberg.border.primary}`,
              }}
            />
            {availableDates.map((d) => {
              const isSelected = dateFilter === d;
              const dayDiff = Math.round((new Date().setHours(0,0,0,0) - new Date(d + 'T00:00:00').getTime()) / 86400000);
              const dayLabel = dayDiff === 0 ? 'Today' : dayDiff === 1 ? 'Yesterday' : d.slice(5);
              return (
                <Chip
                  key={d}
                  size="small"
                  label={dayLabel}
                  onClick={() => setDateFilter(isSelected ? 'all' : d)}
                  sx={{
                    height: 22, fontSize: 10, cursor: 'pointer',
                    bgcolor: isSelected ? bloomberg.accent.orangeDim : bloomberg.bg.tertiary,
                    color: isSelected ? bloomberg.accent.orange : dayDiff <= 1 ? bloomberg.status.positive : dayDiff <= 3 ? bloomberg.text.secondary : bloomberg.status.negative,
                    border: `1px solid ${isSelected ? bloomberg.accent.orange : bloomberg.border.primary}`,
                    fontWeight: dayDiff <= 1 ? 700 : 400,
                  }}
                />
              );
            })}
          </Box>
        )}
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {[...Array(10)].map((_, i) => <Skeleton key={i} variant="rounded" height={36} />)}
        </Box>
      ) : filtered.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: bloomberg.text.muted }}>
            No signals detected for the current filters.
          </Typography>
        </Paper>
      ) : (
        categoryOrder.filter((cat) => grouped[cat]).map((cat) => (
          <Paper key={cat} sx={{ mb: 2, overflow: 'hidden' }}>
            <Box sx={{ px: 2, py: 1, bgcolor: bloomberg.bg.tertiary, borderBottom: `2px solid ${bloomberg.accent.orange}` }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: bloomberg.accent.orange, fontSize: 12, letterSpacing: '0.05em' }}>
                {CATEGORY_LABELS[cat] || cat} ({grouped[cat].length})
              </Typography>
            </Box>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
              <Box component="thead">
                <Box component="tr">
                  {['Symbol', 'Signal', 'Date', 'Price', 'Chg%', 'RSI', 'Sector', 'Country'].map((h) => (
                    <Box
                      key={h}
                      component="th"
                      sx={{
                        textAlign: h === 'Symbol' || h === 'Signal' || h === 'Sector' ? 'left' : 'right',
                        py: 0.6, px: 1.5, fontSize: 10, fontWeight: 600,
                        color: bloomberg.text.muted, textTransform: 'uppercase',
                        letterSpacing: '0.06em', borderBottom: `1px solid ${bloomberg.border.primary}`,
                      }}
                    >
                      {h}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box component="tbody">
                {grouped[cat].map((sig, idx) => (
                  <Box
                    key={`${sig.symbol}-${sig.signal_type}-${idx}`}
                    component="tr"
                    sx={{
                      borderBottom: `1px solid ${bloomberg.border.primary}`,
                      '&:hover': { bgcolor: bloomberg.bg.hover },
                    }}
                  >
                    <Box component="td" sx={{ py: 0.6, px: 1.5, fontSize: 12 }}>
                      <TickerLink symbol={sig.symbol} fontWeight={700} fontSize={12} />
                      <Typography component="span" sx={{ ml: 1, fontSize: 10, color: bloomberg.text.muted }}>
                        {sig.name.length > 20 ? sig.name.slice(0, 20) + '...' : sig.name}
                      </Typography>
                    </Box>
                    <Box component="td" sx={{ py: 0.6, px: 1.5, fontSize: 11 }}>
                      <Chip
                        size="small"
                        icon={sig.direction === 'bullish'
                          ? <TrendingUpIcon sx={{ fontSize: '12px !important' }} />
                          : <TrendingDownIcon sx={{ fontSize: '12px !important' }} />
                        }
                        label={SIGNAL_LABELS[sig.signal_type] || sig.signal_type}
                        sx={{
                          height: 20,
                          fontSize: 10,
                          fontWeight: 700,
                          bgcolor: sig.direction === 'bullish' ? bloomberg.status.positiveBg : bloomberg.status.negativeBg,
                          color: sig.direction === 'bullish' ? bloomberg.status.positive : bloomberg.status.negative,
                          '& .MuiChip-icon': {
                            color: sig.direction === 'bullish' ? bloomberg.status.positive : bloomberg.status.negative,
                          },
                        }}
                      />
                    </Box>
                    {/* Date column — color-coded by staleness */}
                    <Box component="td" sx={{ py: 0.6, px: 1.5, textAlign: 'right' }}>
                      {(() => {
                        if (!sig.signal_date) return <Typography component="span" sx={{ fontSize: 10, color: bloomberg.text.muted }}>—</Typography>;
                        const d = new Date(sig.signal_date + 'T00:00:00');
                        const today = new Date(); today.setHours(0, 0, 0, 0);
                        const diffDays = Math.round((today.getTime() - d.getTime()) / 86400000);
                        return (
                          <Typography component="span" sx={{
                            fontSize: 10, fontFamily: '"IBM Plex Mono", monospace',
                            color: diffDays <= 1 ? bloomberg.status.positive : diffDays <= 3 ? bloomberg.text.secondary : bloomberg.status.negative,
                            fontWeight: diffDays <= 1 ? 700 : 400,
                          }}>
                            {sig.signal_date.slice(5)}
                          </Typography>
                        );
                      })()}
                    </Box>
                    <Box component="td" sx={{ py: 0.6, px: 1.5, fontSize: 11, fontFamily: '"IBM Plex Mono", monospace', textAlign: 'right', fontWeight: 500 }}>
                      {sig.price?.toFixed(2) || '—'}
                    </Box>
                    <Box component="td" sx={{
                      py: 0.6, px: 1.5, fontSize: 11, fontFamily: '"IBM Plex Mono", monospace', textAlign: 'right', fontWeight: 500,
                      color: sig.change_pct > 0 ? bloomberg.status.positive : sig.change_pct < 0 ? bloomberg.status.negative : bloomberg.text.primary,
                    }}>
                      {sig.change_pct > 0 ? '+' : ''}{sig.change_pct?.toFixed(2) || '0.00'}%
                    </Box>
                    <Box component="td" sx={{
                      py: 0.6, px: 1.5, fontSize: 11, fontFamily: '"IBM Plex Mono", monospace', textAlign: 'right',
                      color: sig.rsi != null ? (sig.rsi > 70 ? bloomberg.status.negative : sig.rsi < 30 ? bloomberg.status.positive : bloomberg.text.secondary) : bloomberg.text.muted,
                    }}>
                      {sig.rsi != null ? sig.rsi.toFixed(1) : '—'}
                    </Box>
                    <Box component="td" sx={{ py: 0.6, px: 1.5, fontSize: 10, color: bloomberg.text.secondary, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {sig.sector || '—'}
                    </Box>
                    <Box component="td" sx={{ py: 0.6, px: 1.5, fontSize: 10, fontFamily: '"IBM Plex Mono", monospace', textAlign: 'right', color: bloomberg.text.muted }}>
                      {sig.country}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Paper>
        ))
      )}

      </>)}

      {/* ── History tab ─────────────────────────────────────────────────────── */}
      {tab === 'history' && (
        <Box>
          {/* History filters */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              select size="small" label="Category"
              value={histCategory}
              onChange={(e) => setHistCategory(e.target.value)}
              sx={{ minWidth: 130, '& .MuiInputBase-root': { fontSize: 12 } }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="milestone">Milestones</MenuItem>
              <MenuItem value="ma_cross">MA Crosses</MenuItem>
              <MenuItem value="momentum">Momentum</MenuItem>
            </TextField>
            <TextField
              select size="small" label="Direction"
              value={histDirection}
              onChange={(e) => setHistDirection(e.target.value)}
              sx={{ minWidth: 120, '& .MuiInputBase-root': { fontSize: 12 } }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="bullish">Bullish</MenuItem>
              <MenuItem value="bearish">Bearish</MenuItem>
            </TextField>
            <TextField
              size="small" label="From" type="date"
              value={histDateFrom}
              onChange={(e) => setHistDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 150, '& .MuiInputBase-root': { fontSize: 12 } }}
            />
            <TextField
              size="small" label="To" type="date"
              value={histDateTo}
              onChange={(e) => setHistDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 150, '& .MuiInputBase-root': { fontSize: 12 } }}
            />
            <Chip
              size="small" label="Search"
              onClick={fetchHistory}
              sx={{ height: 30, fontSize: 11, fontWeight: 700, cursor: 'pointer', bgcolor: bloomberg.accent.orangeDim, color: bloomberg.accent.orange, border: `1px solid ${bloomberg.accent.orange}` }}
            />
            {/* Sector filter for history */}
            <TextField
              select size="small" label="Sector"
              value={sectorFilter}
              onChange={(e) => setSectorFilter(e.target.value)}
              sx={{ minWidth: 160, '& .MuiInputBase-root': { fontSize: 11 } }}
            >
              <MenuItem value="all" sx={{ fontSize: 12 }}>All Sectors</MenuItem>
              {availableSectors.map((s) => (
                <MenuItem key={s} value={s} sx={{ fontSize: 12 }}>{s}</MenuItem>
              ))}
            </TextField>
            {/* Region filter for history */}
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography variant="caption" sx={{ color: bloomberg.text.muted, mr: 0.5, fontWeight: 600, fontSize: 10 }}>
                REGION:
              </Typography>
              {Object.keys(REGION_MAP).map((region) => {
                const isOn = activeRegions.has(region);
                return (
                  <Chip
                    key={region}
                    size="small"
                    label={region}
                    onClick={() => toggleRegion(region)}
                    sx={{
                      height: 22, fontSize: 10, cursor: 'pointer', fontWeight: isOn ? 700 : 400,
                      bgcolor: isOn ? bloomberg.accent.orangeDim : bloomberg.bg.tertiary,
                      color: isOn ? bloomberg.accent.orange : bloomberg.text.secondary,
                      border: `1px solid ${isOn ? bloomberg.accent.orange : bloomberg.border.primary}`,
                    }}
                  />
                );
              })}
            </Box>
            {filteredHist.length > 0 && (
              <Typography variant="caption" sx={{ color: bloomberg.text.muted }}>
                {filteredHist.length} result{filteredHist.length !== 1 ? 's' : ''}
              </Typography>
            )}
          </Box>

          {histLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {[...Array(8)].map((_, i) => <Skeleton key={i} variant="rounded" height={36} />)}
            </Box>
          ) : filteredHist.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: bloomberg.text.muted }}>
                No historical signals found. Try adjusting filters or wait for the next scan to persist data.
              </Typography>
            </Paper>
          ) : (
            <Paper sx={{ overflow: 'hidden' }}>
              <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                <Box component="thead">
                  <Box component="tr">
                    {['Date', 'Symbol', 'Signal', 'Direction', 'Price', 'Chg%', 'RSI', 'Country'].map((h) => (
                      <Box key={h} component="th" sx={{
                        textAlign: h === 'Symbol' || h === 'Signal' || h === 'Direction' ? 'left' : 'right',
                        py: 0.6, px: 1.5, fontSize: 10, fontWeight: 600,
                        color: bloomberg.text.muted, textTransform: 'uppercase',
                        letterSpacing: '0.06em', borderBottom: `1px solid ${bloomberg.border.primary}`,
                        bgcolor: bloomberg.bg.tertiary,
                      }}>
                        {h}
                      </Box>
                    ))}
                  </Box>
                </Box>
                <Box component="tbody">
                  {filteredHist.map((sig, idx) => (
                    <Box key={`${sig.symbol}-${sig.signal_type}-${sig.signal_date}-${idx}`} component="tr" sx={{
                      borderBottom: `1px solid ${bloomberg.border.primary}`,
                      '&:hover': { bgcolor: bloomberg.bg.hover },
                    }}>
                      <Box component="td" sx={{ py: 0.6, px: 1.5, fontSize: 10, fontFamily: '"IBM Plex Mono", monospace', textAlign: 'right', color: bloomberg.text.secondary, fontWeight: 500 }}>
                        {sig.signal_date}
                      </Box>
                      <Box component="td" sx={{ py: 0.6, px: 1.5, fontSize: 12 }}>
                        <TickerLink symbol={sig.symbol} fontWeight={700} fontSize={12} />
                        <Typography component="span" sx={{ ml: 1, fontSize: 10, color: bloomberg.text.muted }}>
                          {(sig.name || '').length > 18 ? sig.name.slice(0, 18) + '…' : sig.name}
                        </Typography>
                      </Box>
                      <Box component="td" sx={{ py: 0.6, px: 1.5, fontSize: 10, color: bloomberg.text.secondary }}>
                        {SIGNAL_LABELS[sig.signal_type] || sig.signal_type}
                      </Box>
                      <Box component="td" sx={{ py: 0.6, px: 1.5, fontSize: 10 }}>
                        <Chip size="small"
                          icon={sig.direction === 'bullish'
                            ? <TrendingUpIcon sx={{ fontSize: '11px !important' }} />
                            : <TrendingDownIcon sx={{ fontSize: '11px !important' }} />}
                          label={sig.direction}
                          sx={{
                            height: 18, fontSize: 9, fontWeight: 700,
                            bgcolor: sig.direction === 'bullish' ? bloomberg.status.positiveBg : bloomberg.status.negativeBg,
                            color: sig.direction === 'bullish' ? bloomberg.status.positive : bloomberg.status.negative,
                            '& .MuiChip-icon': { color: sig.direction === 'bullish' ? bloomberg.status.positive : bloomberg.status.negative },
                          }}
                        />
                      </Box>
                      <Box component="td" sx={{ py: 0.6, px: 1.5, fontSize: 11, fontFamily: '"IBM Plex Mono", monospace', textAlign: 'right', fontWeight: 500 }}>
                        {sig.price?.toFixed(2) || '—'}
                      </Box>
                      <Box component="td" sx={{
                        py: 0.6, px: 1.5, fontSize: 11, fontFamily: '"IBM Plex Mono", monospace', textAlign: 'right', fontWeight: 500,
                        color: sig.change_pct > 0 ? bloomberg.status.positive : sig.change_pct < 0 ? bloomberg.status.negative : bloomberg.text.primary,
                      }}>
                        {sig.change_pct != null ? `${sig.change_pct > 0 ? '+' : ''}${sig.change_pct.toFixed(2)}%` : '—'}
                      </Box>
                      <Box component="td" sx={{
                        py: 0.6, px: 1.5, fontSize: 11, fontFamily: '"IBM Plex Mono", monospace', textAlign: 'right',
                        color: sig.rsi != null ? (sig.rsi > 70 ? bloomberg.status.negative : sig.rsi < 30 ? bloomberg.status.positive : bloomberg.text.secondary) : bloomberg.text.muted,
                      }}>
                        {sig.rsi != null ? sig.rsi.toFixed(1) : '—'}
                      </Box>
                      <Box component="td" sx={{ py: 0.6, px: 1.5, fontSize: 10, fontFamily: '"IBM Plex Mono", monospace', textAlign: 'right', color: bloomberg.text.muted }}>
                        {sig.country}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Paper>
          )}
        </Box>
      )}


      </Box>{/* end exportRef */}
    </Box>
  );
};

export default Signals;
